import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useMutation } from '@apollo/client';
import { Form, Formik } from 'formik';
import React from 'react';
import { Button } from '~/components/ui/button';
import { toast } from 'react-toastify';
import type {
  AePartsFragment,
  OutcropPartsFragment,
} from '~/apollo/generated/v3/graphql';
import { FormErrors } from '~/components/common/FormErrors';
import { Modal } from '~/components/common/Modal';
import { AEFormFields } from '~/components/upload/architecturalElement/AEFormFields';
import { useModalState } from '~/hooks/modal';
import type { AEFormValues } from '~/utils/modules/architecturalElement';
import {
  initialAEFormValues,
  toAEInputV3,
  validationSchema,
} from '~/utils/modules/architecturalElement';

const UPDATE_AE_MUTATION = gql`
  mutation UpdateAE(
    $id: Int!
    $architecturalElement: ArchitecturalElementInput!
  ) {
    updateArchitecturalElement(
      id: $id
      architecturalElement: $architecturalElement
    ) {
      id
    }
  }
`;

type Props = {
  architecturalElement: AePartsFragment;
  outcrops: OutcropPartsFragment[];
  refetchQueries: PureQueryOptions[];
  children: (showModal: (e: React.MouseEvent) => any) => React.ReactNode;
};

export function UpdateAEModal({
  architecturalElement,
  outcrops,
  refetchQueries,
  children,
}: Props) {
  const { show, showModal, hideModal } = useModalState();

  const [updateAE, { loading, error }] = useMutation(UPDATE_AE_MUTATION, {
    refetchQueries,
  });

  async function handleSubmit(values: AEFormValues) {
    try {
      await updateAE({
        variables: {
          id: architecturalElement.id,
          architecturalElement: toAEInputV3(values),
        },
      });

      hideModal();
      toast.success('Architectural Element updated successfully.');
    } catch (err) {
      console.log('Error updating AE', err);
      toast.error('There was a problem updating the Architectural Element.');
    }
  }

  return (
    <>
      {children(showModal)}

      <Modal size="lg" open={show} onHide={hideModal}>
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialAEFormValues(architecturalElement)}
          validationSchema={validationSchema}
          enableReinitialize
        >
          <Form>
            <Modal.Body heading="Update Architectural Element">
              <div className="space-y-4">
                <AEFormFields action="update" outcrops={outcrops} />
                <FormErrors graphQLError={error} />
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button
                type="button"
                color="ghost"
                onClick={hideModal}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button type="submit" color="primary" disabled={loading}>
                Save
              </Button>
            </Modal.Footer>
          </Form>
        </Formik>
      </Modal>
    </>
  );
}
